import { useState } from 'react'
import { FileJson, FileSpreadsheet, FileType, FileCode, FileText, Download, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { apiClient, BASE_URL } from '../api/client'
import type { DeterministicPreview, ExportResponse, UUID } from '../types'

interface Props {
  studyId: UUID
  deterministicPreview?: DeterministicPreview | null
}

const FORMATS = [
  { key: 'clinspark_json', label: 'ClinSpark Package', desc: 'Deterministic CMM build package', icon: FileJson },
  { key: 'json', label: 'JSON', desc: 'Machine-readable study design', icon: FileJson },
  { key: 'csv', label: 'CSV', desc: 'Tabular data for spreadsheets', icon: FileSpreadsheet },
  { key: 'xlsx', label: 'Excel', desc: 'Multi-sheet workbook', icon: FileSpreadsheet },
  { key: 'odm_xml', label: 'ODM XML', desc: 'CDISC ODM format', icon: FileCode },
  { key: 'annotated_crf_html', label: 'Annotated CRF', desc: 'HTML annotated case report form', icon: FileType },
]

export default function StudyExportTab({ studyId, deterministicPreview }: Props) {
  const [selectedFormat, setSelectedFormat] = useState('json')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ExportResponse | null>(null)

  const handleExport = async () => {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await apiClient.post<ExportResponse>(`/studies/${studyId}/export`, {
        format: selectedFormat,
        options: {},
      })
      setResult(res)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setLoading(false)
    }
  }

  const selected = FORMATS.find((f) => f.key === selectedFormat)

  const downloadJson = (filename: string, payload: unknown) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Export Study</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Choose a format and generate an export package for the study.
        </p>
      </div>

      <div className="panel p-4 space-y-3">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white">Deterministic parser artifacts</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">Download the exact parsed package or prepared payload used for review and debugging.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" className="btn-secondary" disabled={!deterministicPreview?.clin_spark_study_package} onClick={() => deterministicPreview?.clin_spark_study_package && downloadJson(`study_${studyId}_parsed_result.json`, deterministicPreview.clin_spark_study_package)}>
            <Download className="h-4 w-4 mr-1.5" aria-hidden={true} /> Parsed result package
          </button>
          <button type="button" className="btn-secondary" disabled={!deterministicPreview} onClick={() => deterministicPreview && downloadJson(`study_${studyId}_prepared_payload.json`, deterministicPreview)}>
            <Download className="h-4 w-4 mr-1.5" aria-hidden={true} /> Prepared payload
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FORMATS.map((fmt) => {
          const Icon = fmt.icon
          const isSelected = selectedFormat === fmt.key
          return (
            <button
              key={fmt.key}
              onClick={() => setSelectedFormat(fmt.key)}
              className={`p-4 rounded-lg border text-left transition-colors focus-visible ${
                isSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              aria-pressed={isSelected}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-primary-600" aria-hidden={true} />
                <span className="font-medium text-gray-900 dark:text-white">{fmt.label}</span>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{fmt.desc}</div>
            </button>
          )
        })}
      </div>

      <div className="panel p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <FileText className="h-4 w-4 text-gray-500" aria-hidden={true} />
          Selected format: <span className="font-medium text-gray-900 dark:text-white">{selected?.label}</span>
        </div>
        <div className="flex items-center gap-3">
          {error && (
            <div className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="h-4 w-4" aria-hidden={true} />
              {error}
            </div>
          )}
          <button
            onClick={handleExport}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden={true} />
                Exporting…
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-1.5" aria-hidden={true} />
                Export
              </>
            )}
          </button>
        </div>
      </div>

      {result && (
        <div className="panel p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-green-800 dark:text-green-300">
            <CheckCircle2 className="h-5 w-5" aria-hidden={true} />
            Export ready
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
            <div>
              Filename: <code className="bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded">{result.filename}</code>
            </div>
            <div>Size: {(result.size_bytes / 1024).toFixed(1)} KB</div>
          </div>
          <a
            href={`${BASE_URL}${result.download_url}`}
            className="btn-primary bg-green-600 hover:bg-green-700 inline-flex"
            download
          >
            <Download className="h-4 w-4 mr-1.5" aria-hidden={true} />
            Download
          </a>
        </div>
      )}
    </div>
  )
}
